import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { DeletedUserBadge } from "@/components/DeletedUserBadge";
import { toast } from "@/components/Toast";
import { Plus, Search } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { REQUEST_TYPE_LABEL, formatDateTimeAR } from "@/lib/i18n";
import { ExportButtons } from "@/components/ExportButtons"; // Fix BS

// Fix BQ (2026-11-03): pestañas por estado, buscador y quién aprobó/rechazó (pedido del cliente).
const TABS = [
  { key: "pendiente", label: "Pendientes" },
  { key: "aprobada", label: "Aprobadas" },
  { key: "rechazada", label: "Rechazadas" },
  { key: "todas", label: "Todas" },
];
const norm = (s) => (s || "").toString().normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

export default function AdminRequests() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState(null);
  const [tab, setTab] = useState("pendiente");
  const [q, setQ] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/internal-requests"); setList(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const resolve = async (id, status) => {
    const note = window.prompt(`Comentario opcional para ${status}:`);
    if (note === null) return; // canceló
    try { await api.post(`/internal-requests/${id}/resolve`, { status, admin_response: note || "" }); toast.success(`Solicitud ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const counts = useMemo(() => {
    const c = { pendiente: 0, aprobada: 0, rechazada: 0, todas: (list || []).length };
    (list || []).forEach((r) => { if (c[r.status] != null) c[r.status] += 1; });
    return c;
  }, [list]);

  const shown = useMemo(() => {
    const needle = norm(q.trim());
    return (list || []).filter((r) => (tab === "todas" || r.status === tab) && (!needle || [
      r.description, r.supervisor_name, REQUEST_TYPE_LABEL[r.type] || r.type, r.admin_response, r.resolved_by_name,
    ].some((v) => norm(v).includes(needle))));
  }, [list, tab, q]);

  return (
    <div style={{ padding: "20px 24px 60px" }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 16, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "#BF3437", fontWeight: 700, textTransform: "uppercase" }}>Equipo</div>
          <h1 style={{ fontSize: 32, fontWeight: 900, margin: "4px 0 0", color: "#0A0A0A", letterSpacing: "-0.02em" }}>Solicitudes internas</h1>
        </div>
        <Link to={`/${user?.role}/solicitud`} className="btn-primary" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }} data-testid="req-new-btn"><Plus size={14} style={{ marginRight: 6 }} />Nueva solicitud</Link>
      </div>
      <p style={{ fontSize: 13, color: "var(--invict-text-muted)", margin: "0 0 14px" }} data-testid="req-list-subtitle">Consultá y hacé seguimiento de las solicitudes cargadas por el equipo.</p>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={`req-tab-${t.key}`}
            style={{ padding: "7px 12px", borderRadius: 18, fontSize: 12, fontWeight: 800, cursor: "pointer",
              border: `1.5px solid ${tab === t.key ? "#0A0A0A" : "var(--invict-bone-3)"}`,
              background: tab === t.key ? "#0A0A0A" : "#fff", color: tab === t.key ? "#fff" : "#0A0A0A" }}>
            {t.label} · {counts[t.key]}
          </button>
        ))}
        <div style={{ flex: "1 1 220px", display: "flex", alignItems: "center", border: "1px solid var(--invict-bone-3)", background: "#fff", padding: "0 10px", borderRadius: 4 }}>
          <Search size={14} style={{ color: "var(--invict-text-muted)" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por texto, persona o tipo" style={{ flex: 1, border: "none", padding: "9px 8px", outline: "none" }} data-testid="req-search"/>
        </div>
        <ExportButtons kind="requests" rows={shown} testIdPrefix="req" filtersLabel={[TABS.find((t) => t.key === tab)?.label, q && `Búsqueda: "${q}"`].filter(Boolean).join(" · ")} />
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {list == null && <div style={{ padding: 20 }}><div className="skel" style={{ height: 60 }} /></div>}
        {list && shown.length === 0 && <div style={{ padding: 40, textAlign: "center", color: "var(--invict-text-muted)" }}>Sin solicitudes.</div>}
        {shown.map((r) => (
          <div key={r.id} style={{ padding: "14px 20px", borderBottom: "1px solid var(--invict-bone-3)", display: "grid", gridTemplateColumns: "auto 1fr auto auto", gap: 12, alignItems: "center" }} data-testid={`req-${r.id}`}>
            <span className="badge gray">{REQUEST_TYPE_LABEL[r.type] || r.type}</span>
            <div>
              <div style={{ fontSize: 13, color: "#0A0A0A" }}>{r.description}</div>
              <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 4 }}>{r.supervisor_name}{r.supervisor_deleted && <DeletedUserBadge id={r.id} />} · {formatDateTimeAR(r.created_at)}</div>
              {r.status !== "pendiente" && (
                <div style={{ fontSize: 11, color: "#0A0A0A", fontWeight: 700, marginTop: 4 }} data-testid={`req-resolved-by-${r.id}`}>
                  {r.status === "aprobada" ? "Aprobada" : "Rechazada"}{r.resolved_by_name ? ` por ${r.resolved_by_name}` : ""}{r.resolved_at ? ` · ${formatDateTimeAR(r.resolved_at)}` : ""}
                </div>
              )}
              {r.admin_response && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 4 }}>Respuesta: {r.admin_response}</div>}
            </div>
            <span className={`badge ${r.status === "pendiente" ? "amber" : r.status === "aprobada" ? "green" : "red"}`}>{r.status === "pendiente" ? "Pendiente" : r.status === "aprobada" ? "Aprobada" : "Rechazada"}</span>
            {r.status === "pendiente" && isAdmin ? (
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => resolve(r.id, "aprobada")} className="btn-ghost" style={{ padding: "5px 10px", fontSize: 10, color: "#16a34a", borderColor: "#16a34a" }} data-testid={`req-approve-${r.id}`}>Aprobar</button>
                <button onClick={() => resolve(r.id, "rechazada")} className="btn-ghost" style={{ padding: "5px 10px", fontSize: 10, color: "#BF3437", borderColor: "#BF3437" }} data-testid={`req-reject-${r.id}`}>Rechazar</button>
              </div>
            ) : <span />}
          </div>
        ))}
      </div>
    </div>
  );
}
